import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import useNavigateWithParams from '../../hooks/useNavigateWithParams';
import { useAuth } from '../../context/AuthContext';
import { useGuide } from '../../context/GuideContext';
import { useTheme } from '../../context/ThemeContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import GuideCard from '../../components/GuideCard/GuideCard';
import SearchBar from '../../components/SearchBar/SearchBar';
import EmptyState from '../../components/EmptyState/EmptyState';
import LivePreview from '../../components/LivePreview/LivePreview';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../services/api';
import { generatePreviewThumbnail } from '../../utils/thumbnailGenerator';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigateWithParams();
  const location = useLocation();
  const { user, token, isGuestUser, jwtToken } = useAuth();
  const { theme } = useTheme();
  const {
    drafts,
    publishedGuides,
    loading,
    fetchDrafts,
    fetchPublishedGuides,
    createNewGuide,
    duplicateGuide,
    deleteGuide,
    publishGuide,
  } = useGuide();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [draftsExpanded, setDraftsExpanded] = useState(true);
  const [publishedExpanded, setPublishedExpanded] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: '', message: '' });

  // Export PDF state
  const [exportGuide, setExportGuide] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const exportPreviewRef = useRef(null);
  const hasExportedRef = useRef(false);

  // Thumbnail generation state for duplicated guides
  const [thumbnailGuide, setThumbnailGuide] = useState(null);
  const thumbnailPreviewRef = useRef(null);
  const hasGeneratedThumbnailRef = useRef(false);

  useEffect(() => {
    fetchDrafts();
    fetchPublishedGuides();
  }, [fetchDrafts, fetchPublishedGuides]);

  // Force refresh when navigating back from builder
  useEffect(() => {
    if (location.state?.refresh) {
      fetchDrafts();
      fetchPublishedGuides();
      // Clear the state to prevent refresh on next navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, fetchDrafts, fetchPublishedGuides]);

  // Helper to show popup
  const showPopup = (type, message) => {
    setPopup({ show: true, type, message });
  };

  // Close popup manually
  const closePopup = () => {
    setPopup({ show: false, type: '', message: '' });
  };

  // Helper function to check if a Quill field is empty
  const isQuillFieldEmpty = (html) => {
    if (!html) return true;
    const cleaned = html
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '')
      .replace(/<br\s*\/?>/gi, '')
      .replace(/&nbsp;/gi, '')
      .replace(/\s+/g, '')
      .trim();
    return cleaned.length === 0;
  };

  // Helper function to check if all required guide fields are filled
  const isGuideComplete = (guide) => {
    if (!guide) return false;

    // Check therapeutic area (select field)
    if (!guide.therapeutic_area) return false;

    // Check required Quill fields
    if (isQuillFieldEmpty(guide.title)) return false;
    if (isQuillFieldEmpty(guide.event_location)) return false;
    if (isQuillFieldEmpty(guide.disclaimers)) return false;

    return true;
  };

  // Helper function to check if a study has all required fields filled
  const isStudyFieldsComplete = (study) => {
    if (!study) return false;

    // Check required fields for study
    if (!study.type) return false;
    if (isQuillFieldEmpty(study.name)) return false;
    if (!study.icon) return false;
    if (isQuillFieldEmpty(study.title)) return false;
    if (isQuillFieldEmpty(study.author_name)) return false;
    if (isQuillFieldEmpty(study.objective)) return false;
    if (isQuillFieldEmpty(study.blurb)) return false;
    if (isQuillFieldEmpty(study.references)) return false;
    if (!study.presentation_link || study.presentation_link.trim() === '') return false;
    if (!study.prescribing_link || study.prescribing_link.trim() === '') return false;

    return true;
  };

  // Check if all studies in the guide are complete
  const areAllStudiesComplete = (guide) => {
    if (!guide || !guide.studies || guide.studies.length === 0) return true;
    return guide.studies.every(study => isStudyFieldsComplete(study));
  };

  // Get validation error message for publishing
  const getPublishValidationError = (guide) => {
    if (!guide) return 'No guide to publish.';

    // Check guide fields
    const missingGuideFields = [];
    if (!guide.therapeutic_area) missingGuideFields.push('Therapeutic Area');
    if (isQuillFieldEmpty(guide.title)) missingGuideFields.push('Quick Guide Title');
    if (isQuillFieldEmpty(guide.event_location)) missingGuideFields.push('Event Name & Location');
    if (isQuillFieldEmpty(guide.disclaimers)) missingGuideFields.push('Disclaimer(s)');

    // Check study fields
    const incompleteStudies = [];
    if (guide.studies && guide.studies.length > 0) {
      guide.studies.forEach((study, index) => {
        const missingFields = [];
        if (!study.type) missingFields.push('Study type');
        if (isQuillFieldEmpty(study.name)) missingFields.push('Study Name');
        if (!study.icon) missingFields.push('Study Icon');
        if (isQuillFieldEmpty(study.title)) missingFields.push('Study Title');
        if (isQuillFieldEmpty(study.author_name)) missingFields.push('Study Author Name(s) and Credentials');
        if (isQuillFieldEmpty(study.objective)) missingFields.push('Objective');
        if (isQuillFieldEmpty(study.blurb)) missingFields.push('Summary of Results');
        if (isQuillFieldEmpty(study.references)) missingFields.push('References');
        if (!study.presentation_link || study.presentation_link.trim() === '') missingFields.push('Presentation Link');
        if (!study.prescribing_link || study.prescribing_link.trim() === '') missingFields.push('Prescribing Information Link');

        if (missingFields.length > 0) {
          incompleteStudies.push(`Study ${index + 1}: ${missingFields.join(', ')}`);
        }
      });
    }

    // Build error message
    let errorMessage = 'Please fill in all required fields before publishing:\n\n';
    if (missingGuideFields.length > 0) {
      errorMessage += `Event Details: ${missingGuideFields.join(', ')}\n\n`;
    }
    if (incompleteStudies.length > 0) {
      errorMessage += incompleteStudies.join('\n');
    }

    return errorMessage;
  };

  const filteredDrafts = useMemo(() => {
    // if (searchQuery.length < 4) return drafts;
    if (!searchQuery) return drafts;
    const query = searchQuery.toLowerCase();
    return drafts.filter(
      guide =>
        guide.title?.toLowerCase().includes(query) ||
        guide.description?.toLowerCase().includes(query)
    );
  }, [drafts, searchQuery]);

  const filteredPublished = useMemo(() => {
    // if (searchQuery.length < 4) return publishedGuides;
    if (!searchQuery) return publishedGuides;
    const query = searchQuery.toLowerCase();
    return publishedGuides.filter(
      guide =>
        guide.title?.toLowerCase().includes(query) ||
        guide.description?.toLowerCase().includes(query)
    );
  }, [publishedGuides, searchQuery]);

  const handleNewGuide = () => {
    createNewGuide();
    navigate('/builder/new');
  };

  const handleSelectGuide = (guide, isDraft) => {
    if (selectedGuide?.id === guide.id) {
      setSelectedGuide(null);
    } else {
      setSelectedGuide({ ...guide, isDraft });
    }
  };

  const handleCancel = () => {
    setSelectedGuide(null);
  };

  const handleEdit = () => {
    if (selectedGuide) {
      navigate(`/builder/${selectedGuide.id}`);
    }
  };

  const handleView = () => {
    if (selectedGuide) {
      navigate(`/builder/${selectedGuide.id}?view=true`);
    }
  };

  // Perform PDF export from the rendered LivePreview
  const performPDFExport = useCallback(async () => {
    if (!exportPreviewRef.current || !exportGuide) return;
    if (hasExportedRef.current) {
      console.log('Export already in progress, skipping');
      return;
    }

    hasExportedRef.current = true;

    // Variables for cleanup
    let livePreviewEl = null;
    let pages = [];
    let originalLivePreviewStyle = '';
    let originalStyles = [];

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      const previewContainer = exportPreviewRef.current;
      if (!previewContainer) {
        throw new Error('Preview container not found');
      }

      livePreviewEl = previewContainer.querySelector('.live-preview');
      pages = previewContainer.querySelectorAll('.preview-page');

      if (pages.length === 0) {
        throw new Error('No pages found to export');
      }

      // Add exporting class to live preview
      originalLivePreviewStyle = livePreviewEl?.style.cssText || '';
      if (livePreviewEl) {
        livePreviewEl.classList.add('exporting');
        livePreviewEl.style.width = "665px";
        livePreviewEl.style.minWidth = "665px";
      }

      // Make all pages visible for export (remove hidden-for-slider, add visible)
      pages.forEach((page) => {
        originalStyles.push({
          className: page.className,
          display: page.style.display,
          visibility: page.style.visibility,
          width: page.style.width,
          minWidth: page.style.minWidth,
        });
        page.classList.remove('hidden-for-slider');
        page.classList.add('visible');
        page.style.display = 'flex';
        page.style.visibility = 'visible';
        page.style.width = "665px";
        page.style.minWidth = "665px";
      });

      // Wait for styles to apply
      await new Promise(resolve => setTimeout(resolve, 300));

      const pdfWidth = 210; // mm
      const pageCanvases = [];
      const pageHeights = [];
      const allPdfLinks = [];

      // Capture all pages
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const rect = page.getBoundingClientRect();

        const canvas = await html2canvas(page, {
          scale: 3,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        });

        pageCanvases.push({ canvas, rect });
        pageHeights.push((canvas.height * pdfWidth) / canvas.width);
      }

      // Create PDF with captured pages and collect link data
      const studyStartPages = [];
      let pdf = null;

      for (let i = 0; i < pages.length; i++) {
        const { canvas, rect } = pageCanvases[i];
        const height = pageHeights[i];
        const scaleFactor = pdfWidth / rect.width;

        if (i === 0) {
          pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [pdfWidth, height] });
        } else {
          pdf.addPage([pdfWidth, height]);
        }

        studyStartPages[i] = i + 1;

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, height, undefined, 'FAST');

        // Collect navigation items and external links for this page
        const navItems = [];
        const externalLinks = [];

        pages[i].querySelectorAll('.pdf-nav-item').forEach(item => {
          const r = item.getBoundingClientRect();
          navItems.push({
            left: r.left - rect.left,
            top: r.top - rect.top,
            width: r.width,
            height: r.height,
            targetIndex: parseInt(item.dataset.pageIndex, 10),
          });
        });

        pages[i].querySelectorAll('.pdf-external-link').forEach(link => {
          const r = link.getBoundingClientRect();
          if (link.href) {
            externalLinks.push({
              left: r.left - rect.left,
              top: r.top - rect.top,
              width: r.width,
              height: r.height,
              url: link.href,
            });
          }
        });

        allPdfLinks.push({
          page: i + 1,
          navItems,
          externalLinks,
          scaleFactor,
        });
      }

      // Add clickable regions to PDF
      const isValid = (...n) => n.every(v => Number.isFinite(v) && v >= 0);

      for (const pageLinks of allPdfLinks) {
        const { page, navItems, externalLinks, scaleFactor } = pageLinks;

        pdf.setPage(page);

        // Add internal navigation links
        navItems.forEach(item => {
          const targetPage = studyStartPages[item.targetIndex];
          if (!targetPage || targetPage === page) return;

          const x = item.left * scaleFactor;
          const y = item.top * scaleFactor;
          const w = item.width * scaleFactor;
          const h = item.height * scaleFactor;

          if (!isValid(x, y, w, h) || w === 0 || h === 0) return;

          pdf.link(x, y, w, h, {
            pageNumber: targetPage,
            top: 0, // Navigate to top of page
          });
        });

        // Add external URL links
        externalLinks.forEach(link => {
          const x = link.left * scaleFactor;
          const y = link.top * scaleFactor;
          const w = link.width * scaleFactor;
          const h = link.height * scaleFactor;

          if (!isValid(x, y, w, h) || w === 0 || h === 0) return;

          pdf.link(x, y, w, h, { url: link.url });
        });
      }

      const guideTitle = exportGuide.title
        ? exportGuide.title.replace(/<[^>]*>/g, '').trim().substring(0, 50)
        : 'guide';
      const sanitizedTitle = guideTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${sanitizedTitle}_${timestamp}.pdf`;

      pdf.save(filename);
      showPopup('success', 'PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showPopup('error', 'Error exporting PDF. Please try again.');
    } finally {
      // Restore original styles
      if (livePreviewEl) {
        livePreviewEl.classList.remove('exporting');
        livePreviewEl.style.cssText = originalLivePreviewStyle;
      }

      pages.forEach((page, i) => {
        if (originalStyles[i]) {
          page.className = originalStyles[i].className;
          page.style.display = originalStyles[i].display || '';
          page.style.visibility = originalStyles[i].visibility || '';
          page.style.width = originalStyles[i].width || '';
          page.style.minWidth = originalStyles[i].minWidth || '';
        }
      });

      setExportGuide(null);
      setIsExporting(false);
      setActionLoading(false);
      setTimeout(() => {
        hasExportedRef.current = false;
      }, 2000);
    }
  }, [exportGuide]);

  // Trigger export when exportGuide is set
  useEffect(() => {
    if (exportGuide && isExporting && !hasExportedRef.current) {
      const timer = setTimeout(() => {
        if (exportPreviewRef.current && !hasExportedRef.current) {
          performPDFExport();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [exportGuide, isExporting, performPDFExport]);

  // Generate thumbnail for duplicated guide
  const generateDuplicateThumbnail = useCallback(async () => {
    if (!thumbnailPreviewRef.current || !thumbnailGuide || hasGeneratedThumbnailRef.current) return;

    hasGeneratedThumbnailRef.current = true;

    try {
      // Wait for preview to render
      await new Promise(resolve => setTimeout(resolve, 1500));

      const thumbnailBase64 = await generatePreviewThumbnail('thumbnail-preview-container');
      if (thumbnailBase64 && token) {
        const thumbnailResponse = await api.uploadThumbnail(token, jwtToken, thumbnailGuide.id, thumbnailBase64, user, isGuestUser);
        if (thumbnailResponse && thumbnailResponse.thumbnail_url) {
          // Save guide with new thumbnail
          await api.updateGuide(token, jwtToken, thumbnailGuide.id, {
            ...thumbnailGuide,
            thumbnail_url: thumbnailResponse.thumbnail_url
          }, user, isGuestUser);

          // Refresh drafts to show updated thumbnail
          await fetchDrafts();
        }
      }
    } catch (error) {
      console.error('Error generating thumbnail for duplicate:', error);
    } finally {
      // Clean up
      setThumbnailGuide(null);
      hasGeneratedThumbnailRef.current = false;
    }
  }, [thumbnailGuide, token, jwtToken, user, isGuestUser, fetchDrafts]);

  // Trigger thumbnail generation when thumbnailGuide is set
  useEffect(() => {
    if (thumbnailGuide && !hasGeneratedThumbnailRef.current) {
      const timer = setTimeout(() => {
        if (thumbnailPreviewRef.current && !hasGeneratedThumbnailRef.current) {
          generateDuplicateThumbnail();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [thumbnailGuide, generateDuplicateThumbnail]);

  const handleExportPDF = async () => {
    if (!selectedGuide || isExporting) return;

    hasExportedRef.current = false;
    setIsExporting(true);
    setActionLoading(true);

    try {
      const fullGuide = await api.getGuide(token, jwtToken, selectedGuide.id, user, isGuestUser);
      setExportGuide(fullGuide);
    } catch (error) {
      console.error('Error fetching guide for export:', error);
      showPopup('error', 'Error preparing guide for export. Please try again.');
      setIsExporting(false);
      setActionLoading(false);
      hasExportedRef.current = false;
    }
  };

  const handleDuplicate = async () => {
    if (selectedGuide) {
      setActionLoading(true);
      try {
        const result = await duplicateGuide(selectedGuide);

        // Clear selection immediately
        setSelectedGuide(null);

        if (result) {
          // Fetch the full duplicated guide for thumbnail generation
          hasGeneratedThumbnailRef.current = false;
          const fullDuplicatedGuide = await api.getGuide(token, jwtToken, result.id, user, isGuestUser);
          if (fullDuplicatedGuide) {
            // Set the guide to trigger thumbnail generation
            setThumbnailGuide(fullDuplicatedGuide);
          }
        }

        // duplicateGuide already handles fetching and state update with correct title
        // Only do a delayed refresh to ensure eventual consistency
        setTimeout(async () => {
          await Promise.all([fetchDrafts(), fetchPublishedGuides()]);
        }, 2000);

        showPopup('success', 'Guide duplicated successfully! The new draft should appear in Your Drafts section.');
      } catch (error) {
        console.error('Dashboard: Error during duplication:', error);
        showPopup('error', 'Error duplicating guide. Please try again.');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleDelete = () => {
    if (selectedGuide) {
      setShowDeleteModal(true);
    }
  };

  const confirmDelete = async () => {
    if (selectedGuide) {
      setActionLoading(true);
      try {
        await deleteGuide(selectedGuide.id);
        setSelectedGuide(null);
        setShowDeleteModal(false);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handlePublish = () => {
    if (selectedGuide) {
      // Validate guide and studies before showing publish modal
      const isGuideValid = isGuideComplete(selectedGuide);
      const areStudiesValid = areAllStudiesComplete(selectedGuide);

      if (!isGuideValid || !areStudiesValid) {
        const errorMessage = getPublishValidationError(selectedGuide);
        showPopup('error', errorMessage);
        return;
      }

      setShowPublishModal(true);
    }
  };

  const clearSelection = () => {
    if (selectedGuide) setSelectedGuide(null);
  };
  
  const confirmPublish = async () => {
    if (selectedGuide) {
      setActionLoading(true);
      setShowPublishModal(false);
      try {
        await publishGuide(selectedGuide.id);
        setSelectedGuide(null);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const cancelPublish = () => {
    setShowPublishModal(false);
  };

  const hasSearch = !!searchQuery.trim();

  const noResults =
    hasSearch && filteredDrafts.length === 0 && filteredPublished.length === 0;

  return (
    <div className="dashboard" onClick={clearSelection}>
      <Header />
      <main className="dashboard-main">
        <div className="dashboard-container">
          <div className="dashboard-header-section">
            <h1 className="dashboard-title">Select a Quick Guide</h1>
            <div className="dashboard-search-row">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Or search for a guide here"
              />

              {noResults && (
                <span className="no-results-text">No results found</span>
              )}

              <button className="filter-btn" onClick={(e) => e.stopPropagation()}>
                <img src="/filter-icon.svg" alt="Filter" />
                Filter by
              </button>
            </div>
          </div>

          {/* Drafts Section */}
          <section className="guides-section">
            <div
              className="section-header"
              onClick={(e) => {
                e.stopPropagation();
                setDraftsExpanded(!draftsExpanded);
              }}
            >
              <h2>Your Drafts</h2>
              <svg
                className={`expand-icon ${draftsExpanded ? '' : 'expanded'}`}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M18 15L12 9L6 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {draftsExpanded && (
              <div className="guides-grid">
                {/* New Quick Guide Card */}
                <div
                  className="new-guide-card"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNewGuide();
                  }}
                >
                  <div className="new-guide-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <path
                        d="M24 10V38"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                      <path
                        d="M10 24H38"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <span className="new-guide-text">New Quick Guide</span>
                </div>

                {filteredDrafts.map((guide) => (
                  <div
                    key={guide.id}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GuideCard
                      guide={guide}
                      isDraft={true}
                      isSelected={selectedGuide?.id === guide.id}
                      onSelect={() => handleSelectGuide(guide, true)}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Published Section */}
          <section className="guides-section published-guides-section">
            <div
              className="section-header"
              onClick={(e) => {
                e.stopPropagation();
                setPublishedExpanded(!publishedExpanded);
              }}
            >
              <h2>Published Guides</h2>
              <svg
                className={`expand-icon ${publishedExpanded ? 'expanded' : ''}`}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {publishedExpanded && (
              <>
                {filteredPublished.length === 0 ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    {searchQuery ? (
                      <EmptyState
                        icon="search"
                        title="No results found"
                        description={`No published guides match "${searchQuery}"`}
                      />
                    ) : (
                      <EmptyState
                        icon="published"
                        title="No published guides"
                        description="Published guides will appear here once drafts are published"
                      />
                    )}
                  </div>
                ) : (
                  <div className="guides-grid">
                    {filteredPublished.map((guide) => (
                      <div
                        key={guide.id}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <GuideCard
                          guide={guide}
                          isDraft={false}
                          isSelected={selectedGuide?.id === guide.id}
                          onSelect={() => handleSelectGuide(guide, false)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>

      {!selectedGuide && <Footer />}

      {/* Bottom Action Bar */}
      {selectedGuide && (
        <div className="action-bar" onClick={(e) => e.stopPropagation()}>
          <button className="action-bar-btn cancel" onClick={handleCancel}>
            Cancel
          </button>
          <button className="action-bar-btn export" onClick={handleExportPDF}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M9 3V11M9 11L5.5 7.5M9 11L12.5 7.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 13V14.25C3 14.6642 3.33579 15 3.75 15H14.25C14.6642 15 15 14.6642 15 14.25V13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span> Export PDF</span> 
          </button>
          <button className="action-bar-btn duplicate" onClick={handleDuplicate}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_18519_5799)">
                <path
                  d="M5.83203 8.05648C5.83203 7.46704 6.06619 6.90174 6.48299 6.48494C6.89979 6.06814 7.46509 5.83398 8.05453 5.83398H15.2762C15.5681 5.83398 15.8571 5.89147 16.1267 6.00316C16.3964 6.11485 16.6414 6.27856 16.8477 6.48494C17.0541 6.69132 17.2178 6.93632 17.3295 7.20597C17.4412 7.47562 17.4987 7.76462 17.4987 8.05648V15.2782C17.4987 15.57 17.4412 15.859 17.3295 16.1287C17.2178 16.3983 17.0541 16.6433 16.8477 16.8497C16.6414 17.0561 16.3964 17.2198 16.1267 17.3315C15.8571 17.4432 15.5681 17.5007 15.2762 17.5007H8.05453C7.76267 17.5007 7.47366 17.4432 7.20402 17.3315C6.93437 17.2198 6.68936 17.0561 6.48299 16.8497C6.27661 16.6433 6.1129 16.3983 6.00121 16.1287C5.88952 15.859 5.83203 15.57 5.83203 15.2782V8.05648Z"
                  stroke="#AE2185"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.34333 13.9475C3.0875 13.8021 2.87471 13.5916 2.72658 13.3374C2.57846 13.0832 2.50028 12.7942 2.5 12.5V4.16667C2.5 3.25 3.25 2.5 4.16667 2.5H12.5C13.125 2.5 13.465 2.82083 13.75 3.33333"
                  stroke="#AE2185"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.16797 11.6667L10.8346 13.3333L14.168 10"
                  stroke="#AE2185"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_18519_5799">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
             <span> Duplicate</span> 
          </button>

          {selectedGuide.isDraft && (
            <button className="action-bar-btn delete" onClick={handleDelete}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M3 5H15M6 5V3.5C6 3.22386 6.22386 3 6.5 3H11.5C11.7761 3 12 3.22386 12 3.5V5M7.5 8V12.5M10.5 8V12.5M4.5 5L5.25 14.5C5.28979 14.7761 5.51471 15 5.79167 15H12.2083C12.4853 15 12.7102 14.7761 12.75 14.5L13.5 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
               <span> Delete</span> 
            </button>
          )}

          {selectedGuide.isDraft && (
            <button className="action-bar-btn publish" onClick={handlePublish}>
              <svg
                width="14"
                height="10"
                viewBox="0 0 14 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.75 4.91667L4.91667 9.08333L13.25 0.75"
                  stroke="#AE2185"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
                <span> Publish</span> 
            </button>
          )}

          {selectedGuide.isDraft && (
            <button className="action-bar-btn primary" onClick={handleEdit}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M13.5 2.25L15.75 4.5L6.75 13.5H4.5V11.25L13.5 2.25Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
                 Edit
            </button>
          )}

          {!selectedGuide.isDraft && (
            <button className="action-bar-btn view" onClick={handleView}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_18328_6480)">
                  <path
                    d="M8.33203 10.0007C8.33203 10.4427 8.50763 10.8666 8.82019 11.1792C9.13275 11.4917 9.55667 11.6673 9.9987 11.6673C10.4407 11.6673 10.8646 11.4917 11.1772 11.1792C11.4898 10.8666 11.6654 10.4427 11.6654 10.0007C11.6654 9.55862 11.4898 9.1347 11.1772 8.82214C10.8646 8.50958 10.4407 8.33398 9.9987 8.33398C9.55667 8.33398 9.13275 8.50958 8.82019 8.82214C8.50763 9.1347 8.33203 9.55862 8.33203 10.0007Z"
                    stroke="#AE2185"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.5 10C15.5 13.3333 13 15 10 15C7 15 4.5 13.3333 2.5 10C4.5 6.66667 7 5 10 5C13 5 15.5 6.66667 17.5 10Z"
                    stroke="#AE2185"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_18328_6480">
                    <rect width="20" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>
                <span> View</span>
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Are you sure you want to delete?</h2>
            <p className="modal-description">
              Deleting means permanent removal and cannot be recovered.
            </p>
            <div className="modal-actions">
              <button
                className="modal-btn modal-btn-danger"
                onClick={confirmDelete}
              >
                Delete guide
              </button>
              <button className="modal-btn modal-btn-link" onClick={cancelDelete}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishModal && (
        <div className="modal-overlay" onClick={cancelPublish}>
          <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Ready to publish?</h2>
            <p className="modal-description">
              Publishing is final—no more edits can be made.
            </p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-primary" onClick={confirmPublish}>
                Publish guide
              </button>
              <button className="modal-btn modal-btn-link" onClick={cancelPublish}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {(loading || actionLoading) && (
        <div className="loading-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="spinner"></div>
        </div>
      )}

      {/* Success/Error Popup Modal */}
      {popup.show && (
        <div className="popup-overlay">
          <div className={`popup-modal ${popup.type}`}>
            <div className="popup-message">{popup.message}</div>
            <button className="popup-close-btn" onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Hidden LivePreview for PDF Export */}
      {exportGuide && (
        <div
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: '800px',
            zIndex: -1,
            overflow: 'visible',
          }}
        >
          <div ref={exportPreviewRef}>
            <LivePreview
              guide={exportGuide}
              theme={theme}
              isExporting={true}
            />
          </div>
        </div>
      )}

      {/* Hidden LivePreview for Thumbnail Generation (duplicated guides) */}
      {thumbnailGuide && (
        <div
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: '900px',
            zIndex: -1,
            overflow: 'visible',
          }}
        >
          <div ref={thumbnailPreviewRef} id="thumbnail-preview-container">
            <LivePreview
              guide={thumbnailGuide}
              theme={theme}
              isExporting={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;